import React from 'react'
import classNames from 'classnames'
import { func, string, arrayOf } from 'prop-types'
import { isURL } from 'validator'
import { debugSettingsType, compositionType, filterType } from 'models/propTypes'

import Icon from 'components/uiElements/Icon'
import Dropdown from 'components/formElements/Dropdown'
import Range from 'components/formElements/Range'

import * as THREE from 'three'

require('./Debug.scss')

const Debug = ({
    debugSettings,
    updateDebugSettings,
    composition,
    takeScreenshot,
    loseWebglContext,
    disableAdmin,
    updateManifest,
    subCatalogs,
    furnitureFilter,
    updateFurnitureFilter,
    startSwapTest,
    startLoadTest,
}) => {
    const onInputChange = (key, e, isData) => {
        let { value } = e.target
        if (isData) {
            value = JSON.parse(e.target.value)
        }
        updateManifest(composition.sceneId, key, value)
    }

    const subCatalogOptions = [{ key: '*', label: 'All' }]
    if (subCatalogs && subCatalogs.length) {
        subCatalogs.reduce((acc, val) => {
            acc.push({ key: val, label: val })
            return acc
        }, subCatalogOptions)
    }

    const environmentRotationOptions = [
        { key: 0, label: '+Z' },
        { key: 1, label: '-X' },
        { key: 2, label: '-Z' },
        { key: 3, label: '+X' },
    ]

    const toneMappingOptions = [
        { key: THREE.NoToneMapping, label: 'None' },
        { key: THREE.LinearToneMapping, label: 'Linear' },
        { key: THREE.ReinhardToneMapping, label: 'Reinhard' },
        { key: THREE.CineonToneMapping, label: 'Cineon' },
        { key: THREE.ACESFilmicToneMapping, label: 'ACES Filmic' },
    ]

    const swapFiftyItems = () => {
        startSwapTest()
    }

    const trackedMemoryOptions = [
        { key: 0, label: 'JS heap' },
        { key: 1, label: 'WebGLBuffers' },
    ]

    return (
        <div className="Debug">
            <section>
                {composition ? (
                    <div>
                        <h2>Info</h2>
                        <div className="infoRow">
                            <div className="rowTitle">CompositionId</div>
                            <div className="rowContent">
                                {composition.id} {composition.id < 0 ? <span>(new)</span> : null}
                            </div>
                        </div>
                        <div className="infoRow">
                            <div className="rowTitle">SceneId</div>
                            <div className="rowContent">
                                {/* <a href={beLink} target="_blank" rel="noopener noreferrer"> */}
                                {composition.sceneId}
                                {/* </a> */}
                            </div>
                        </div>
                        {composition.scene ? (
                            <div className="infoRow jobId">
                                <div className="rowTitle">JobId</div>
                                <div className="rowContent">{composition.scene.jobId}</div>
                            </div>
                        ) : null}
                        {composition.scene && composition.scene.metaData ? (
                            <div className="infoRow">
                                <div className="rowTitle">MetaData</div>
                                <div className="rowContent">
                                    <a
                                        href={composition.scene.metaUrl}
                                        title={composition.scene.metaUrl}
                                        target="_BLANK"
                                        rel="noopener noreferrer"
                                        key="link"
                                    >
                                        <Icon code="link" />
                                    </a>
                                    <div
                                        title={JSON.stringify(composition.scene.metaData)}
                                        key="info"
                                        className="dataPreview"
                                    >
                                        <Icon code="document" />
                                    </div>
                                    <input
                                        type="text"
                                        value={JSON.stringify(composition.scene.metaData)}
                                        key="toCopy"
                                        readOnly
                                    />
                                </div>
                            </div>
                        ) : null}
                        {composition.scene
                            ? [
                                  <h2 key="manifest">Manifest</h2>,
                                  <div key="manifestItems">
                                      {Object.keys(composition.scene.manifest).map((key) => {
                                          const value = composition.scene.manifest[key]
                                          const isData = typeof value !== 'string'
                                          const content = []

                                          if (!isData && isURL(value)) {
                                              // We have a url
                                              content.push(
                                                  <a
                                                      href={value}
                                                      title={value}
                                                      target="_BLANK"
                                                      rel="noopener noreferrer"
                                                      key="link"
                                                      className="featureLink"
                                                  >
                                                      <Icon code="link" />
                                                  </a>,
                                              )
                                          } else {
                                              // No url
                                              content.push(<Icon code="link" className="warn" key="noFeat" />)
                                          }
                                          if (value) {
                                              if (isData || !isURL(value)) {
                                                  content.push(
                                                      <div
                                                          title={isData ? JSON.stringify(value) : value}
                                                          key="info"
                                                          className="dataPreview"
                                                      >
                                                          <Icon code="document" />
                                                      </div>,
                                                  )
                                              } else {
                                                  // TODO: check for image url specifically
                                                  content.push(
                                                      <div key="image" className="imagePreview">
                                                          <Icon code="image" />
                                                          <div className="imagePreviewThumb">
                                                              <img src={value} alt="" />
                                                          </div>
                                                      </div>,
                                                  )
                                              }
                                              content.push(
                                                  <input
                                                      type="text"
                                                      value={isData ? JSON.stringify(value) : value}
                                                      key="toCopy"
                                                      onChange={(e) => onInputChange(key, e, isData)}
                                                      onKeyDown={(e) => e.stopPropagation()}
                                                  />,
                                              )
                                          }
                                          return (
                                              <div className="infoRow" key={key}>
                                                  <div className="rowTitle">{key}</div>
                                                  <div className="rowContent">{content}</div>
                                              </div>
                                          )
                                      })}
                                  </div>,
                              ]
                            : null}
                    </div>
                ) : null}
                <h2>Catalog</h2>
                <div className="dropRow">
                    <p>
                        Sub-catalog<span>ctrl + 0, 1-5</span>
                    </p>
                    <Dropdown
                        name="subCatalog"
                        onChange={(e) => updateFurnitureFilter({ subCatalog: e.target.value })}
                        value={furnitureFilter.subCatalog}
                        options={subCatalogOptions}
                    />
                </div>
                <h2>Visualization</h2>
                <div className="switchRow">
                    <p>
                        Vanishing lines<span>g</span>
                    </p>
                    <span
                        className={classNames('switch', { on: debugSettings.linesViz })}
                        onClick={() => updateDebugSettings({ linesViz: !debugSettings.linesViz })}
                    />
                </div>
                <div className="switchRow">
                    <p>Light helpers</p>
                    <span
                        className={classNames('switch', { on: debugSettings.lightsViz })}
                        onClick={() => updateDebugSettings({ lightsViz: !debugSettings.lightsViz })}
                    />
                </div>
                <div className="switchRow">
                    <p>Show depth</p>
                    <span
                        className={classNames('switch', { on: debugSettings.depthViz })}
                        onClick={() => updateDebugSettings({ depthViz: !debugSettings.depthViz })}
                    />
                </div>
                <div className="switchRow">
                    <p>Imitate low performance</p>
                    <span
                        className={classNames('switch', { on: debugSettings.imitateLowPerformance })}
                        onClick={() =>
                            updateDebugSettings({ imitateLowPerformance: !debugSettings.imitateLowPerformance })
                        }
                    />
                </div>
                <div className="switchRow">
                    <p>
                        Highlight dropzone on drag<span>h</span>
                    </p>
                    <span
                        className={classNames('switch', { on: debugSettings.highlightPlanes })}
                        onClick={() => updateDebugSettings({ highlightPlanes: !debugSettings.highlightPlanes })}
                    />
                </div>
                <div className="switchRow">
                    <p>
                        Sematic Segmentation<span>z</span>
                    </p>
                    <span
                        className={classNames('switch', { on: debugSettings.ssViz })}
                        onClick={() => updateDebugSettings({ ssViz: !debugSettings.ssViz })}
                    />
                </div>
                <div className="switchRow">
                    <p>
                        Normals Segmentation<span>n</span>
                    </p>
                    <span
                        className={classNames('switch', { on: debugSettings.nsViz })}
                        onClick={() => updateDebugSettings({ nsViz: !debugSettings.nsViz })}
                    />
                </div>
                <div className="switchRow">
                    <p>Normals Visualization on Loaded Items</p>
                    <span
                        className={classNames('switch', { on: debugSettings.normalsViz })}
                        onClick={() => updateDebugSettings({ normalsViz: !debugSettings.normalsViz })}
                    />
                </div>
                <h2>Light controls</h2>
                <div className="switchRow">
                    <p>Asset AO Map intensity multiplier</p>
                    <Range
                        type="range"
                        min={0.0}
                        max={2.0}
                        step={0.01}
                        value={debugSettings.aoMapIntensity}
                        onChange={(value) => updateDebugSettings({ aoMapIntensity: value })}
                        sideLabel
                    />
                </div>
                <div className="switchRow">
                    <p>Asset IBL intensity multiplier</p>
                    <Range
                        type="range"
                        min={0.0}
                        max={2.0}
                        step={0.01}
                        value={debugSettings.environmentIntensity}
                        onChange={(value) => updateDebugSettings({ environmentIntensity: value })}
                        sideLabel
                    />
                </div>
                <div className="dropRow">
                    <p>IBL map rotation:</p>
                    <Dropdown
                        name="environmentRotation"
                        onChange={(e) => updateDebugSettings({ environmentRotation: e.target.value })}
                        value={debugSettings.environmentRotation}
                        options={environmentRotationOptions}
                    />
                </div>
                {composition &&
                composition.scene &&
                composition.scene.manifest &&
                composition.scene.manifest.lighting ? (
                    <div>
                        {composition.scene.manifest.lighting.lights.point
                            ? [
                                  <div key="pointLightIntensities">
                                      {composition.scene.manifest.lighting.lights.point.map((pointLight, index) => (
                                          <div className="switchRow" key={`pointLight${pointLight.id}`}>
                                              <p>Point light [{index}] intensity</p>
                                              <Range
                                                  type="range"
                                                  min={0.0}
                                                  max={pointLight.intensity * 2.0}
                                                  step={pointLight.intensity / 100}
                                                  value={
                                                      pointLight.intensity * debugSettings.pointLightIntensities[index]
                                                  }
                                                  onChange={(value) =>
                                                      updateDebugSettings({
                                                          pointLightIntensities:
                                                              debugSettings.pointLightIntensities.map((v, i) =>
                                                                  index === i ? value / pointLight.intensity : v,
                                                              ),
                                                      })
                                                  }
                                                  sideLabel
                                              />
                                          </div>
                                      ))}
                                  </div>,
                              ]
                            : null}
                        {composition.scene.manifest.lighting.lights.ambient
                            ? [
                                  <div key="ambientLightIntensity">
                                      <div className="switchRow" key="ambientLight0">
                                          <p>Ambient light intensity</p>
                                          <Range
                                              type="range"
                                              min={0.0}
                                              max={composition.scene.manifest.lighting.lights.ambient.intensity * 2.0}
                                              step={composition.scene.manifest.lighting.lights.ambient.intensity / 100}
                                              value={
                                                  composition.scene.manifest.lighting.lights.ambient.intensity *
                                                  debugSettings.ambientLightIntensity
                                              }
                                              onChange={(value) =>
                                                  updateDebugSettings({
                                                      ambientLightIntensity:
                                                          value /
                                                          composition.scene.manifest.lighting.lights.ambient.intensity,
                                                  })
                                              }
                                              sideLabel
                                          />
                                      </div>
                                  </div>,
                              ]
                            : null}
                        {composition.scene.manifest.lighting.lights.directional
                            ? [
                                  <div key="directionalLightIntensities">
                                      {composition.scene.manifest.lighting.lights.directional.map(
                                          (directional, index) => (
                                              <div className="switchRow" key={`directionalLight${directional.id}`}>
                                                  <p>Directional light [{index}] intensity</p>
                                                  <Range
                                                      type="range"
                                                      min={0.0}
                                                      max={directional.intensity * 2.0}
                                                      step={directional.intensity / 100}
                                                      value={
                                                          directional.intensity *
                                                          debugSettings.directionalLightIntensities[index]
                                                      }
                                                      onChange={(value) =>
                                                          updateDebugSettings({
                                                              directionalLightIntensities:
                                                                  debugSettings.directionalLightIntensities.map(
                                                                      (v, i) =>
                                                                          index === i
                                                                              ? value / directional.intensity
                                                                              : v,
                                                                  ),
                                                          })
                                                      }
                                                      sideLabel
                                                  />
                                              </div>
                                          ),
                                      )}
                                  </div>,
                              ]
                            : null}
                    </div>
                ) : null}
                <div className="dropRow">
                    <p>Tone mapping:</p>
                    <Dropdown
                        name="toneMapping"
                        onChange={(e) => updateDebugSettings({ toneMappingType: e.target.value })}
                        value={debugSettings.toneMappingType}
                        options={toneMappingOptions}
                    />
                </div>
                <div className="switchRow">
                    <p>Tone mapping exposure</p>
                    <Range
                        type="range"
                        min={0.0}
                        max={2.0}
                        step={0.01}
                        value={debugSettings.toneMappingExposure}
                        onChange={(value) => updateDebugSettings({ toneMappingExposure: value })}
                        sideLabel
                    />
                </div>
                <div className="switchRow">
                    <p>Enable analytical lights contribution to lightening.</p>
                    <span
                        className={classNames('switch', { on: debugSettings.analyticLightsContribution })}
                        onClick={() => {
                            updateDebugSettings({
                                analyticLightsContribution: !debugSettings.analyticLightsContribution,
                            })
                        }}
                    />
                </div>
                <h2>Settings</h2>
                <div className="switchRow" style={{ display: 'none' }}>
                    <p>
                        Infinite floor plane<span>default to floor plane</span>
                    </p>
                    <span
                        className={classNames('switch', { on: debugSettings.defaultToFloor })}
                        onClick={() => updateDebugSettings({ defaultToFloor: !debugSettings.defaultToFloor })}
                    />
                </div>
                <div className="switchRow">
                    <p>
                        Enable advanced shadows<span>a</span>
                    </p>
                    <span
                        className={classNames('switch', { on: debugSettings.useFancyShadows })}
                        onClick={() => updateDebugSettings({ useFancyShadows: !debugSettings.useFancyShadows })}
                    />
                </div>
                <div className="switchRow">
                    <p>
                        Front side shadow mapping<span>d</span>
                    </p>
                    <span
                        className={classNames('switch', { on: debugSettings.frontSideShadows })}
                        onClick={() => updateDebugSettings({ frontSideShadows: !debugSettings.frontSideShadows })}
                    />
                </div>
                <div className="switchRow">
                    <p>
                        Enable walls<span>w</span>
                    </p>
                    <span
                        className={classNames('switch', { on: debugSettings.enableWalls })}
                        onClick={() => updateDebugSettings({ enableWalls: !debugSettings.enableWalls })}
                    />
                </div>
                <div className="switchRow">
                    <p>
                        Enable GPU stats<span>q</span>
                    </p>
                    <span
                        className={classNames('switch', { on: debugSettings.enableGPUframetimeStats })}
                        onClick={() =>
                            updateDebugSettings({ enableGPUframetimeStats: !debugSettings.enableGPUframetimeStats })
                        }
                    />
                </div>
                <div className="switchRow">
                    <p>
                        Enable memory tracking<span>m</span>
                    </p>
                    <span
                        className={classNames('switch', { on: debugSettings.showMemoryStats })}
                        onClick={() => updateDebugSettings({ showMemoryStats: !debugSettings.showMemoryStats })}
                    />
                </div>
                <div className="dropRow">
                    <p>Tracked memory:</p>
                    <Dropdown
                        name="trackedMemory"
                        onChange={(e) => updateDebugSettings({ trackedMemoryType: e.target.value })}
                        value={debugSettings.trackedMemoryType}
                        options={trackedMemoryOptions}
                    />
                </div>
                <div className="switchRow">
                    <p>
                        Enable art resizing<span>r</span>
                    </p>
                    <span
                        className={classNames('switch', { on: debugSettings.canEditArtSize })}
                        onClick={() => updateDebugSettings({ canEditArtSize: !debugSettings.canEditArtSize })}
                    />
                </div>
                <div className="switchRow">
                    <p>
                        Show extra product details<span>p</span>
                    </p>
                    <span
                        className={classNames('switch', { on: debugSettings.showProductAdminData })}
                        onClick={() =>
                            updateDebugSettings({ showProductAdminData: !debugSettings.showProductAdminData })
                        }
                    />
                </div>
                <div className="switchRow">
                    <p>
                        Use TAA for screenshots<span>p</span>
                    </p>
                    <span
                        className={classNames('switch', { on: debugSettings.useTAAforScreenshots })}
                        onClick={() =>
                            updateDebugSettings({ useTAAforScreenshots: !debugSettings.useTAAforScreenshots })
                        }
                    />
                </div>
                <div className="toolRow">
                    <p>
                        Disable admin privileges<span>ctrl + j</span>
                    </p>
                    <button onClick={disableAdmin}>
                        <Icon code="crossCircle" />
                    </button>
                </div>
                <h2>Tools</h2>
                <div className="toolRow">
                    <p>
                        Take screenshot<span>s</span>
                    </p>
                    <button onClick={takeScreenshot}>
                        <Icon code="camera" />
                    </button>
                </div>
                <div className="toolRow">
                    <p>Lose webgl context</p>
                    <button onClick={loseWebglContext}>
                        <Icon code="crossCircle" />
                    </button>
                </div>
                <div className="toolRow">
                    <p>Furniture swap test</p>
                    <button onClick={swapFiftyItems}>
                        <Icon code="arrowLeftArrowRight" />
                    </button>
                </div>
                <div className="toolRow">
                    <p>Furniture memory leak test</p>
                    <button onClick={startLoadTest}>
                        <Icon code="chevronRight" />
                    </button>
                </div>
            </section>
        </div>
    )
}

Debug.defaultProps = {
    composition: null,
    subCatalogs: null,
}

Debug.propTypes = {
    debugSettings: debugSettingsType.isRequired,
    updateDebugSettings: func.isRequired,
    composition: compositionType,
    takeScreenshot: func.isRequired,
    loseWebglContext: func.isRequired,
    disableAdmin: func.isRequired,
    updateManifest: func.isRequired,
    subCatalogs: arrayOf(string),
    furnitureFilter: filterType.isRequired,
    updateFurnitureFilter: func.isRequired,
    startSwapTest: func.isRequired,
    startLoadTest: func.isRequired,
}

export default Debug
